import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useDebounce from "./useDebounce";

/**
 * URL-as-source-of-truth state for list pages (search, filters, pagination).
 *
 * The URL is the committed state: deep links, refreshes and back/forward all
 * restore the exact view. Text inputs keep a live local value and commit into
 * the URL after a debounce; selects commit immediately. Default values
 * (page 1, default pageSize, empty filters) are omitted so URLs stay clean.
 */

export type TableFilterMode = "debounced" | "immediate";

export interface UseTableUrlStateOptions<K extends string> {
  /**
   * Filter param names and how each one commits to the URL. Must be static
   * for the lifetime of the component (defined outside the render).
   */
  filters: Readonly<Record<K, TableFilterMode>>;
  /** Omitted from the URL when the table sits on this size. Default 10. */
  defaultPageSize?: number;
  /**
   * Prefix applied to every param (e.g. "p" → ptitle/ppage), so two tables
   * on the same page never collide.
   */
  prefix?: string;
  /** Delay for "debounced" filters. Default 500 ms. */
  debounceMs?: number;
}

export interface UseTableUrlStateResult<K extends string> {
  /** Committed page (from the URL). */
  page: number;
  /** Committed page size (from the URL). */
  pageSize: number;
  /** Committed filter values (from the URL) — feed these to the query. */
  filters: Record<K, string>;
  /** Live input values — bind these to the controlled inputs. */
  inputs: Record<K, string>;
  setFilter: (key: K, value: string) => void;
  onPageChange: (page: number, pageSize: number) => void;
}

export const parsePositiveIntParam = (
  raw: string | null,
  fallback: number,
): number => {
  const parsed = parseInt(raw ?? "", 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

/**
 * Merges filter values into the params: non-empty values are set, empty ones
 * removed, and any change resets the page param (a new filter means a new
 * result set, so keeping the old page would show a random slice). Returns the
 * SAME instance when nothing changed so callers can skip the navigation.
 */
export const syncFiltersIntoParams = (
  prev: URLSearchParams,
  filters: Readonly<Record<string, string>>,
  pageParam: string,
): URLSearchParams => {
  const unchanged = Object.entries<string>(filters).every(
    ([param, value]) => (prev.get(param) ?? "") === value,
  );
  if (unchanged) return prev;

  const next = new URLSearchParams(prev);
  for (const [param, value] of Object.entries<string>(filters)) {
    if (value) next.set(param, value);
    else next.delete(param);
  }
  next.delete(pageParam);
  return next;
};

/**
 * Writes page/pageSize into the params, omitting default values (page 1 and
 * the default page size) to keep URLs clean. Returns the SAME instance when
 * nothing changed.
 */
export const syncPageIntoParams = (
  prev: URLSearchParams,
  args: {
    page: number;
    pageSize: number;
    defaultPageSize: number;
    pageParam: string;
    pageSizeParam: string;
  },
): URLSearchParams => {
  const pageValue = args.page > 1 ? String(args.page) : null;
  const pageSizeValue =
    args.pageSize !== args.defaultPageSize ? String(args.pageSize) : null;
  if (
    prev.get(args.pageParam) === pageValue &&
    prev.get(args.pageSizeParam) === pageSizeValue
  ) {
    return prev;
  }

  const next = new URLSearchParams(prev);
  if (pageValue) next.set(args.pageParam, pageValue);
  else next.delete(args.pageParam);
  if (pageSizeValue) next.set(args.pageSizeParam, pageSizeValue);
  else next.delete(args.pageSizeParam);
  return next;
};

/**
 * Same-tick write composition. react-router's `setSearchParams` builds the
 * next URL from the searchParams of the RENDER (even in its functional
 * form), so two writers flushing in the same React commit — e.g. the two
 * hook instances on the Courses page when both debounces expire together —
 * would each rebuild the URL from the same stale base and the second one
 * would drop the first one's params. Every write therefore composes on top
 * of the last params written this tick; the buffer clears on the next
 * microtask, once the batch reached the router. `window.location` is not an
 * alternative base: MemoryRouter (tests) never updates it.
 */
let pendingWrite: URLSearchParams | null = null;
// Serialized params the pending chain started from. The pending buffer is
// only trusted while the caller's render params still equal this base: once
// a render reflects the write, the buffer is structurally stale and ignored
// (this cannot rely on a microtask clear alone — jest's fake timers fake
// queueMicrotask, so a time-based clear may never run inside a test tick).
let pendingWriteBase: string | null = null;

const composeSearchWrite = (
  renderParams: URLSearchParams,
  compute: (base: URLSearchParams) => URLSearchParams,
): URLSearchParams | null => {
  const renderKey = renderParams.toString();
  const base =
    pendingWrite !== null && pendingWriteBase === renderKey
      ? pendingWrite
      : renderParams;
  const next = compute(base);
  // Identity means "nothing to change" — no navigation needed.
  if (next === base) return null;
  pendingWrite = next;
  pendingWriteBase = renderKey;
  // Belt in real browsers: drop the buffer as soon as the batch reached the
  // router, so a later navigation back to the exact same search string can
  // never resurrect it.
  queueMicrotask(() => {
    pendingWrite = null;
    pendingWriteBase = null;
  });
  return next;
};

export function useTableUrlState<K extends string>(
  options: UseTableUrlStateOptions<K>,
): UseTableUrlStateResult<K> {
  // The config is a static literal at every call site; freezing the first
  // value keeps effect dependencies simple and honest.
  const { filters: filterModes, defaultPageSize = 10, prefix = "", debounceMs = 500 } =
    useRef(options).current;
  const filterKeys = useRef(Object.keys(filterModes) as K[]).current;
  const paramName = useCallback((key: string) => `${prefix}${key}`, [prefix]);
  const pageParam = paramName("page");
  const pageSizeParam = paramName("pageSize");

  const [searchParams, setSearchParams] = useSearchParams();

  const readFilters = (params: URLSearchParams): Record<K, string> => {
    const values = {} as Record<K, string>;
    for (const key of filterKeys) {
      values[key] = params.get(paramName(key)) ?? "";
    }
    return values;
  };

  const page = parsePositiveIntParam(searchParams.get(pageParam), 1);
  const pageSize = parsePositiveIntParam(
    searchParams.get(pageSizeParam),
    defaultPageSize,
  );
  const filters = readFilters(searchParams);

  // URL → inputs on mount; afterwards the inputs lead and the URL follows.
  const [inputs, setInputs] = useState<Record<K, string>>(() =>
    readFilters(searchParams),
  );
  const debouncedInputs = useDebounce(inputs, debounceMs);
  // Refs so the effects below can compare against the latest values without
  // depending on them (which would re-run the sync on every keystroke).
  const debouncedInputsRef = useRef(debouncedInputs);
  debouncedInputsRef.current = debouncedInputs;
  const inputsRef = useRef(inputs);
  inputsRef.current = inputs;
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  // Debounced inputs → URL. `replace` keeps typing from flooding the
  // history. The composed write returns null when nothing changed (mount,
  // adoption of an external URL change, or a commit that matches the URL
  // already), skipping the navigation entirely.
  const lastCommittedDebouncedRef = useRef<Record<K, string> | null>(null);
  useEffect(() => {
    // Commit only when the debounced values actually changed. The effect
    // also re-fires when react-router hands out a new setSearchParams (its
    // identity changes on every navigation); committing then would write a
    // STALE debounced value mid-debounce-window over fresher URL state
    // (e.g. wiping the page param a click just set).
    if (lastCommittedDebouncedRef.current === debouncedInputs) return;
    lastCommittedDebouncedRef.current = debouncedInputs;

    const target: Record<string, string> = {};
    for (const key of filterKeys) {
      if (filterModes[key] === "debounced") {
        target[paramName(key)] = debouncedInputs[key];
      }
    }
    const next = composeSearchWrite(searchParamsRef.current, (base) =>
      syncFiltersIntoParams(base, target, pageParam),
    );
    if (next) setSearchParams(next, { replace: true });
  }, [
    debouncedInputs,
    filterKeys,
    filterModes,
    pageParam,
    paramName,
    setSearchParams,
  ]);

  // External URL changes (back/forward, links) → inputs. A param that
  // differs from what this hook last committed was changed by someone else,
  // so the inputs adopt it; comparing against the committed (debounced)
  // value keeps a mid-typing input from being stomped by its own commit.
  useEffect(() => {
    setInputs((current) => {
      let changed = false;
      const next = { ...current };
      for (const key of filterKeys) {
        const urlValue = searchParams.get(paramName(key)) ?? "";
        const committed =
          filterModes[key] === "immediate"
            ? current[key]
            : debouncedInputsRef.current[key];
        if (urlValue !== committed && urlValue !== next[key]) {
          next[key] = urlValue;
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [searchParams, filterKeys, filterModes, paramName]);

  const setFilter = useCallback(
    (key: K, value: string) => {
      setInputs((current) =>
        current[key] === value ? current : { ...current, [key]: value },
      );
      if (filterModes[key] === "immediate") {
        const next = composeSearchWrite(searchParamsRef.current, (base) =>
          syncFiltersIntoParams(base, { [paramName(key)]: value }, pageParam),
        );
        if (next) setSearchParams(next, { replace: true });
      }
    },
    [filterModes, pageParam, paramName, setSearchParams],
  );

  // Page changes push a history entry on purpose: back/forward walking
  // through pages is part of "never lose your place".
  const onPageChange = useCallback(
    (newPage: number, newPageSize: number) => {
      // Flush any half-typed debounced filter FIRST: committing it now (with
      // its page reset) and applying the click on top respects the order of
      // the user's intentions. Otherwise the debounce would expire after the
      // click and its page reset would silently discard the page change.
      // The eventual debounce commit then matches the URL and is a no-op.
      const flushTarget: Record<string, string> = {};
      for (const key of filterKeys) {
        if (filterModes[key] === "debounced") {
          flushTarget[paramName(key)] = inputsRef.current[key];
        }
      }
      const flushed = composeSearchWrite(searchParamsRef.current, (base) =>
        syncFiltersIntoParams(base, flushTarget, pageParam),
      );
      if (flushed) setSearchParams(flushed, { replace: true });

      const next = composeSearchWrite(searchParamsRef.current, (base) =>
        syncPageIntoParams(base, {
          page: newPage,
          pageSize: newPageSize,
          defaultPageSize,
          pageParam,
          pageSizeParam,
        }),
      );
      if (next) setSearchParams(next);
    },
    [
      defaultPageSize,
      filterKeys,
      filterModes,
      pageParam,
      pageSizeParam,
      paramName,
      setSearchParams,
    ],
  );

  return { page, pageSize, filters, inputs, setFilter, onPageChange };
}
